import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, LineChart, Lightbulb, Database, Cloud, Shield, Cpu, CheckCircle2, TrendingUp, Clock, Zap, HelpCircle, ChevronDown } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const keyMetrics = [
  {
    icon: TrendingUp,
    value: '85%',
    label: 'Average Model Accuracy',
    description: 'Across 50+ ML models in production environments'
  },
  {
    icon: Brain,
    value: '200+',
    label: 'AI Projects Delivered',
    description: 'Comprehensive solutions spanning multiple industries'
  },
  {
    icon: Clock,
    value: '6 months',
    label: 'Average ROI Timeline',
    description: 'Typical time to achieve measurable business impact'
  },
  {
    icon: CheckCircle2,
    value: '99%',
    label: 'Client Satisfaction',
    description: 'Sustained partnerships and repeat engagements'
  }
];

const capabilities = [
  {
    title: 'Predictive Analytics & Machine Learning',
    icon: Brain,
    items: [
      'Demand Forecasting: AI-driven prediction models for inventory optimization and production planning',
      'Risk Assessment: Healthcare, financial, and operational risk prediction with early warning systems',
      'Customer Analytics: Behavior prediction, churn analysis, and personalization algorithms',
      'Maintenance Prediction: Predictive maintenance models reducing downtime and operational costs'
    ]
  },
  {
    title: 'Advanced Data Science Solutions',
    icon: Lightbulb,
    items: [
      'Deep Learning Models: Neural networks for complex pattern recognition and automated decision-making',
      'Natural Language Processing: Document analysis, sentiment analysis, and automated content processing',
      'Computer Vision: Image analysis, quality control automation, and visual inspection systems',
      'Time Series Analysis: Trend analysis, seasonality detection, and forecasting optimization'
    ]
  },
  {
    title: 'Business Intelligence & Visualization',
    icon: LineChart,
    items: [
      'Real-Time Dashboards: Power BI, Tableau, and custom analytics platforms for executive insights',
      'Self-Service Analytics: Empowering business users with intuitive data exploration and reporting tools',
      'Performance Monitoring: KPI tracking, automated alerting, and exception management systems',
      'Regulatory Reporting: Automated compliance reporting and audit trail generation'
    ]
  }
];

const techStack = [
  {
    category: 'Machine Learning Platforms',
    items: [
      { name: 'Azure Machine Learning', logo: '/tech-logos/azure-logo.svg' },
      { name: 'TensorFlow', logo: '/tech-logos/tensorflow.svg' },
      { name: 'Scikit-learn', logo: '/tech-logos/scikit-learn.svg' },
      { name: 'Python', logo: '/tech-logos/python.svg' }
    ]
  },
  {
    category: 'Cloud Analytics Platforms',
    items: [
      { name: 'Azure Synapse', logo: '/tech-logos/azure-logo.svg' },
      { name: 'Databricks', logo: '/tech-logos/databricks.svg' },
      { name: 'Power BI', logo: '/tech-logos/power-bi.png' },
      { name: 'Snowflake', logo: '/tech-logos/snowflake.svg' }
    ]
  },
  {
    category: 'Specialized AI Tools',
    items: [
      { name: 'Apache Kafka', logo: '/tech-logos/apache-kafka.svg' },
      { name: 'OpenAI', logo: '/tech-logos/openai.svg' },
      { name: 'Docker', logo: '/tech-logos/docker.svg' },
      { name: 'Tableau', logo: '/tech-logos/tableau.svg' }
    ]
  }
];

const industries = [
  {
    title: 'Healthcare & Life Sciences',
    icon: Shield,
    applications: [
      'Clinical Decision Support',
      'Medical Image Analysis',
      'Drug Discovery',
      'Population Health'
    ]
  },
  {
    title: 'Manufacturing & Industrial IoT',
    icon: Cpu,
    applications: [
      'Quality Control',
      'Supply Chain Optimization',
      'Predictive Maintenance',
      'Process Optimization'
    ]
  },
  {
    title: 'Financial Services & Insurance',
    icon: Database,
    applications: [
      'Fraud Detection',
      'Credit Risk Assessment',
      'Algorithmic Trading',
      'Customer Segmentation'
    ]
  },
  {
    title: 'Retail & E-commerce',
    icon: LineChart,
    applications: [
      'Price Optimization',
      'Inventory Management',
      'Customer Experience',
      'Sales Forecasting'
    ]
  }
];

const successStories = [
  {
    tag: 'Healthcare',
    title: 'Healthcare Network - Heart Disease Prediction',
    metrics: [
      '85% accuracy in predicting heart disease risk 30 days in advance',
      '20% reduction in cardiac readmissions',
      '100+ clinician hours freed monthly'
    ],
    link: '/case-studies/healthcare-ai'
  },
  {
    tag: 'Manufacturing',
    title: 'Manufacturing Company - Demand Forecasting',
    metrics: [
      '88% improvement in forecast accuracy',
      '40% reduction in safety stock levels',
      'Virtually eliminated stock-out incidents'
    ],
    link: '/case-studies/manufacturing-ai'
  }
];

const methodology = [
  {
    title: 'Data Strategy & Assessment',
    description: 'Comprehensive evaluation of data assets and AI opportunities',
    icon: Database
  },
  {
    title: 'Model Development & Training',
    description: 'Custom AI model development with advanced training protocols',
    icon: Brain
  },
  {
    title: 'MLOps & Deployment',
    description: 'Robust deployment with continuous monitoring and updates',
    icon: Cloud
  },
  {
    title: 'Integration & Scaling',
    description: 'Seamless integration with existing systems and scalability planning',
    icon: Cpu
  },
  {
    title: 'Monitoring & Optimization',
    description: 'Continuous performance monitoring and model refinement',
    icon: TrendingUp
  }
];

const whyChooseUs = [
  {
    title: 'Advanced Technical Expertise',
    description: 'Deep specialization in ML, deep learning, and advanced analytics',
    icon: Brain
  },
  {
    title: 'Domain Knowledge',
    description: 'Industry-specific AI solutions with comprehensive understanding',
    icon: Lightbulb
  },
  {
    title: 'End-to-End Capabilities',
    description: 'Complete AI journey from strategy through deployment',
    icon: Cloud
  },
  {
    title: 'Proven Results',
    description: 'Measurable impact across 200+ implementations',
    icon: Shield
  }
];

const faqs = [
  {
    question: 'How long does AI implementation take?',
    answer: 'Our rapid implementation approach delivers AI and ML solutions in 4-12 weeks depending on complexity. Predictive analytics: 4-6 weeks. NLP and document processing: 6-8 weeks. Custom deep learning models: 8-12 weeks. We\'re 60-80% faster than traditional consulting firms through agile methodologies and lean teams.'
  },
  {
    question: 'What makes Agility\'s implementation approach different?',
    answer: 'We deploy senior data scientists and ML engineers directly on your project no layers of account managers or junior staff. Our agile squads ship production-ready AI models in weeks, not months, with continuous delivery and real-time collaboration. Every week you see working models, not presentations.'
  },
  {
    question: 'Do you provide support after deployment?',
    answer: 'Yes, we provide 24/7 post-implementation support including continuous model monitoring, performance optimization, and maintenance. Our support packages ensure your AI solutions continue delivering value with proactive retraining and issue resolution.'
  },
  {
    question: 'What industries do you serve with AI solutions?',
    answer: 'We serve multiple industries including healthcare (risk prediction, diagnostics), manufacturing (demand forecasting, quality control), finance (fraud detection, risk assessment), retail (customer analytics, pricing optimization), and logistics (route optimization, delivery prediction).'
  }
];

const ArtificialIntelligence = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  return (
    <>
      <SEOHead
        title="AI & Machine Learning Solutions | Agility"
        description="Expert AI and machine learning consulting services. Predictive analytics, NLP, computer vision, and custom ML models. 200+ successful AI implementations."
        keywords="AI consulting, machine learning solutions, predictive analytics, data science, deep learning, NLP, computer vision, AI strategy"
        url="https://www.agilitytech.ai/solutions/artificial-intelligence"
      />
      <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1639322537504-6427a16b0a28?auto=format&fit=crop&q=80"
            alt="AI Banner"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
              <span className="text-teal-400 text-sm font-medium">Artificial Intelligence & Analytics</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Artificial Intelligence & Advanced Analytics
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-3xl">
              Unlock the power of your data through cutting-edge AI and machine learning solutions 
              that deliver predictive insights, automate decision-making, and drive competitive 
              advantage across your organization.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-medium 
                  hover:bg-teal-400 transition-all duration-300 inline-flex items-center space-x-2
                  shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <span>Schedule Your AI Strategy Session</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
              <button
                className="bg-white/10 text-white px-8 py-4 rounded-lg text-lg font-medium 
                  hover:bg-white/20 transition-all duration-300 inline-flex items-center space-x-2
                  border border-white/20 hover:border-white/30"
              >
                <span>Download AI Solutions Overview</span>
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Key Metrics Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyMetrics.map((metric, index) => (
              <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 
                border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center">
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <metric.icon className="h-6 w-6 text-teal-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                <p className="text-gray-400 text-sm">{metric.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI & Analytics Capabilities Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            AI & Analytics Capabilities
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 
                  border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300
                  transform hover:-translate-y-1"
              >
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center 
                  justify-center mb-6">
                  <capability.icon className="h-6 w-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{capability.title}</h3>
                <ul className="space-y-3">
                  {capability.items.map((item, idx) => (
                    <li key={idx} className="flex items-start space-x-3 text-gray-300">
                      <CheckCircle2 className="h-5 w-5 text-teal-400 flex-shrink-0 mt-1" />
                      <span className="text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            Technology Stack
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {techStack.map((category, index) => (
              <div key={index} className="space-y-6">
                <h3 className="text-xl font-bold text-teal-400 mb-4 text-center">{category.category}</h3>
                <div className="grid grid-cols-1 gap-4">
                  {category.items.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center space-x-3 bg-gray-800/50 rounded-lg p-4
                        border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300"
                    >
                      <img
                        src={item.logo}
                        alt={`${item.name} logo`}
                        className="h-8 w-8 object-contain"
                      />
                      <span className="text-gray-300 font-medium">{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry-Specific AI Solutions Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            Industry-Specific AI Solutions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 
                  border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300
                  transform hover:-translate-y-1"
              >
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center 
                  justify-center mb-6">
                  <industry.icon className="h-6 w-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{industry.title}</h3>
                <ul className="space-y-2">
                  {industry.applications.map((app, idx) => (
                    <li key={idx} className="flex items-center space-x-2 text-gray-300">
                      <ArrowRight className="h-4 w-4 text-teal-400" />
                      <span className="text-sm">{app}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured AI Success Stories Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            Featured AI Success Stories
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {successStories.map((story, index) => (
              <div
                key={index}
                className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50
                  hover:bg-gray-800/70 transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="text-sm text-teal-400 font-semibold mb-4">{story.tag}</div>
                <h3 className="text-2xl font-bold text-white mb-6">{story.title}</h3>
                <div className="space-y-4 mb-6">
                  {story.metrics.map((metric, idx) => (
                    <div key={idx} className="flex items-center space-x-3 text-gray-300">
                      <CheckCircle2 className="h-5 w-5 text-green-400" />
                      <span className="text-sm">{metric}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to={story.link}
                  className="inline-flex items-center text-teal-400 hover:text-teal-300
                    transition-colors font-medium"
                >
                  <span>Read Full Case Study</span>
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Development Methodology Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            AI Development Methodology
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {methodology.map((step, index) => (
              <div
                key={index}
                className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 
                  border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-8 h-8 bg-teal-500/10 rounded-full flex items-center 
                    justify-center text-teal-400 font-bold">
                    {index + 1}
                  </div>
                  <div className="w-8 h-8 bg-teal-500/10 rounded-lg flex items-center justify-center">
                    <step.icon className="h-5 w-5 text-teal-400" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{step.title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Agility.AI Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            Why Choose Agility.AI
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((reason, index) => (
              <div
                key={index}
                className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50
                  hover:bg-gray-800/70 transition-all duration-300 text-center"
              >
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center 
                  justify-center mb-6 mx-auto">
                  <reason.icon className="h-6 w-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{reason.title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <HelpCircle className="h-12 w-12 text-teal-400 mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-400">
                Everything you need to know about our AI implementation process
              </p>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-900/70 transition-all duration-300"
                  >
                    <span className="text-lg font-semibold text-white pr-8">{faq.question}</span>
                    <ChevronDown
                      className={`h-5 w-5 text-teal-400 flex-shrink-0 transition-transform duration-300 ${
                        openFaqIndex === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openFaqIndex === index && (
                    <div className="px-6 pb-5">
                      <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Brain className="h-8 w-8 text-teal-400" />
            </div>
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Unlock AI-Powered Insights?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Transform Data into Competitive Advantage. Contact our AI specialists to discover how 
              artificial intelligence can accelerate your business growth and innovation.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4 
                rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group
                shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <span>Schedule Your AI Strategy Session</span>
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default ArtificialIntelligence;